/**
 * A browser puts the computed style of the copied selection into the clipboard, so pasted
 * markup can carry an invisible colour — white text copied off a dark page stays white on
 * the editor's white background, where the author can neither see nor delete it.
 *
 * Such markup is recognisable: nothing in the editor writes caret-color or
 * -webkit-text-stroke, and Word and Google Docs label their own leftovers with classes.
 */
const CLIPBOARD_STYLE_FINGERPRINT = /caret-color|-webkit-text-stroke/i;

/**
 * Only payloads that came from another application are rewritten. Markup copied inside the
 * editor is left completely alone, because re-parsing a fragment (a few table cells, say)
 * would drop the tags the HTML parser refuses to keep on their own.
 */
const EXTERNAL_PASTE_MARKERS = [
    CLIPBOARD_STYLE_FINGERPRINT,
    /mso-|class="?Mso/i,
    /(ryNqvb|jCAhz|ChMk0b)/,
    /<meta\s+name=["']?Generator/i,
];

/** Classes that mean nothing in a mail template. */
const FOREIGN_CLASS = /^(Mso|ryNqvb|jCAhz|ChMk0b|gmail_|x_)/i;

const INLINE_TAGS = ['SPAN', 'B', 'I', 'EM', 'STRONG', 'FONT', 'U'];

const isExternalPaste = (html: string): boolean =>
    EXTERNAL_PASTE_MARKERS.some((marker) => marker.test(html));

export const sanitizePastedHtml = (html: string): string => {
    if (!html || !isExternalPaste(html)) {
        return html;
    }

    const doc = new DOMParser().parseFromString(html, 'text/html');

    doc.querySelectorAll('style, link, meta, script').forEach((node) => node.remove());

    // The whole declaration block is dropped, not just the colour: it is a computed-style
    // dump of the source page, so none of it belongs in a mail template.
    doc.querySelectorAll('[style]').forEach((node) => {
        if (CLIPBOARD_STYLE_FINGERPRINT.test(node.getAttribute('style') ?? '')) {
            node.removeAttribute('style');
        }
    });

    doc.querySelectorAll('[class]').forEach((node) => {
        const kept = (node.getAttribute('class') ?? '')
            .split(/\s+/)
            .filter((name) => name && !FOREIGN_CLASS.test(name));

        if (kept.length) {
            node.setAttribute('class', kept.join(' '));
        } else {
            node.removeAttribute('class');
        }
    });

    // Wrappers left behind once their class is gone.
    doc.querySelectorAll(INLINE_TAGS.join(', ')).forEach((node) => {
        if (!node.attributes.length && !node.textContent?.trim() && !node.children.length) {
            node.remove();
        }
    });

    return doc.body.innerHTML;
};

export default sanitizePastedHtml;
